import { Venue, VenueEvent } from 'types/model'

import { useVenue } from '@modules/venue/data'
import { getVenuePurchaseRange } from '@modules/venue/utils'
import { useVenueEvents } from '@modules/event/data'
import { EventCard } from '@modules/event/views/event-card'
import { Carousel } from '@components/carousel'
import { utcToISO } from '@utils/date-time'

export interface EventCarouselProps {
  venueId: string
  layout?: 'vertical' | 'horizontal'
  venue?: Venue
  events?: VenueEvent[]
  onCardClick: (eventId: string) => void
}

export function EventCarousel({
  venueId,
  layout = 'vertical',
  venue: _venue,
  events: _events,
  onCardClick,
}: EventCarouselProps) {
  const { venue, events } = useEventCarouselController(venueId, {
    fallbackData: { venue: _venue, events: _events },
  })

  const venueData = venue.data

  if (venueData === undefined || !events.data)
    return (
      <Carousel>
        {Array(6)
          .fill(0)
          .map((_, index) => (
            <EventCard.Skeleton key={index} />
          ))}
      </Carousel>
    )
  return (
    <div className="w-full">
      {venueData !== undefined && (
        <Carousel itemClassName="min-w-full xs:min-w-min">
          {events.data.map((venueEvent) => (
            <EventCard
              key={venueEvent.id}
              name={venueEvent.name}
              eventId={venueEvent.id}
              dateTime={utcToISO(venueEvent.dateStart)}
              poster={venueEvent.primaryImage?.filename}
              venueName={venueData.name}
              venueAddress={venueData.location.address}
              layout={layout}
              onClick={onCardClick}
            />
          ))}
        </Carousel>
      )}
    </div>
  )
}

function useEventCarouselController(
  venueId: string,
  { fallbackData }: { fallbackData?: { venue?: Venue; events?: VenueEvent[] } } = {}
) {
  const { venue: _venue, events: _events } = fallbackData ?? {}

  const venue = useVenue(venueId, {
    config: {
      fallbackData: _venue,
    },
  })
  const purchaseWindow = venue.data?.inventoryTimeframe ?? 30

  const [dateStart, dateEnd] = getVenuePurchaseRange(venue.data)
  const events = useVenueEvents(venueId, {
    params: {
      dateStart,
      dateEnd,
      limit: purchaseWindow * 2,
      skip: 0,
      sort: 'dateStart',
    },
    config: {
      fallbackData: _events,
    },
  })

  return {
    venue,
    events,
  }
}
